import { NavLink } from 'react-router';

import { IconTile, focusRingOnDark } from '@/design-system';
import { cn } from '@/shared/lib';

import type { NavItemModel, RailCount } from './nav-model';
import { useRailCounts } from './rail-counts';

export interface RailItemProps {
    item: NavItemModel;
    /** Sluit de drawer nadat je hebt genavigeerd. */
    onNavigate?: () => void;
}

/**
 * Eén regel in de rail.
 *
 * Het actieve item is een vlak in `--rail-item-active` met een accentstaaf van
 * 2px aan de linkerkant. Allebei staan ze absoluut *achter* de tekst, precies
 * zoals in het prototype: zo verspringt de tekst niet als je van item wisselt.
 *
 * Voor het label staat het icoon van het merkblad op een tegel: een lichte lijn
 * op inkt, en bij het actieve item een mint lijn op merkgroen. De tegel is 26px,
 * dus de regel blijft even hoog als zonder icoon.
 *
 * `NavLink` zet zelf `aria-current="page"` op het actieve item.
 *
 * `data-tour="nav-<sleutel>"` is het anker van de rondleiding (`features/tour`):
 * een vaste plek, ook als het label ooit anders gaat heten.
 */
export function RailItem({ item, onNavigate }: RailItemProps) {
    const counts = useRailCounts();
    const count = item.count ? counts[item.count.key] : undefined;

    return (
        <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-tour={`nav-${item.key}`}
            className={({ isActive }) =>
                cn(
                    'group relative flex items-center gap-[10px] rounded-[var(--radius-control)]',
                    'py-[5px] pr-[10px] pl-[6px] text-left no-underline',
                    'font-[family-name:var(--font-body)] text-[14px] leading-[1.25]',
                    'cursor-pointer text-[var(--rail-item)]',
                    !isActive && 'hover:bg-[var(--rail-item-hover)]',
                    focusRingOnDark,
                )
            }
        >
            {({ isActive }) => (
                <>
                    {isActive ? (
                        <>
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 rounded-[var(--radius-control)] bg-[var(--rail-item-active)]"
                            />
                            <span
                                aria-hidden="true"
                                className="absolute left-0 w-[var(--rail-marker-w)] rounded-[2px] bg-[var(--rail-item-marker)]"
                                style={{
                                    top: 'var(--rail-marker-inset)',
                                    bottom: 'var(--rail-marker-inset)',
                                }}
                            />
                        </>
                    ) : null}

                    <IconTile
                        name={item.icon}
                        size="sm"
                        surface="dark"
                        active={isActive}
                        className="relative"
                    />

                    <span className="relative min-w-0">{item.label}</span>

                    {item.count && count !== undefined ? (
                        <RailCountBadge count={item.count} value={count} />
                    ) : null}
                </>
            )}
        </NavLink>
    );
}

const BADGE_TONE: Record<RailCount['tone'], string> = {
    accent: 'rounded-[var(--radius-badge)] px-[5px] py-[2px] bg-[var(--rail-badge-accent-bg)] text-[var(--rail-badge-accent-fg)]',
    amber: 'rounded-[var(--radius-badge)] px-[5px] py-[2px] bg-[var(--rail-badge-amber-bg)] text-[var(--rail-badge-amber-fg)]',
    neutral: 'text-[var(--rail-muted)]',
};

/** Het getal rechts in het item. Losse cijfers zeggen een schermlezer niets, dus
 *  er staat een volledige zin naast die alleen hij te horen krijgt. */
function RailCountBadge({ count, value }: { count: RailCount; value: number }) {
    return (
        <span className="relative ml-auto shrink-0 font-[family-name:var(--font-mono)] text-[10px] leading-[1.2]">
            <span aria-hidden="true" className={BADGE_TONE[count.tone]}>
                {value}
            </span>
            <span className="sr-only">{count.describe(value)}</span>
        </span>
    );
}
